import { Link } from "react-router-dom";
import { Cookie, Settings2, X } from "lucide-react";
import { useCookieConsent } from "../context/CookieConsentContext";
import { COOKIE_CATEGORIES, COOKIE_POLICY_VERSION } from "../lib/cookieConsent";
import { useState, useEffect } from "react";

export default function CookieConsentBanner() {
  const {
    bannerOpen,
    prefsOpen,
    setPrefsOpen,
    setBannerOpen,
    acceptAll,
    rejectAll,
    savePreferences,
    consent,
    decided,
  } = useCookieConsent();

  const [draft, setDraft] = useState({
    functional: consent.functional,
    analytics: consent.analytics,
    thirdParty: consent.thirdParty,
  });

  useEffect(() => {
    setDraft({
      functional: consent.functional,
      analytics: consent.analytics,
      thirdParty: consent.thirdParty,
    });
  }, [consent.functional, consent.analytics, consent.thirdParty, prefsOpen]);

  if (!bannerOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-brand-dark/35 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white border border-brand-soft shadow-xl overflow-hidden">
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-brand-soft flex items-center justify-center shrink-0">
              <Cookie size={20} className="text-brand-primary" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-dark/45">
                Aviso cookies
              </p>
              <h2 id="cookie-consent-title" className="font-display text-lg font-extrabold text-brand-dark">
                Uso de cookies
              </h2>
            </div>
          </div>
          {decided ? (
            <button
              type="button"
              onClick={() => {
                setBannerOpen(false);
                setPrefsOpen(false);
              }}
              className="p-1.5 rounded-lg text-brand-dark/40 hover:bg-brand-soft"
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          ) : null}
        </div>

        <div className="px-5 pb-5 space-y-4">
          <p className="text-sm text-brand-dark/75 leading-relaxed">
            Este sitio utiliza cookies técnicas necesarias y, si usted lo permite, cookies
            funcionales, <strong>analítica de visitas (totales y por IP)</strong> y contenido de
            terceros (vídeos, mapas y catálogos). Los datos de{" "}
            <strong>formularios</strong> (contacto, cotización, quejas) se tratan según el{" "}
            <Link to="/privacidad" className="text-brand-primary font-semibold underline underline-offset-2">
              aviso de privacidad
            </Link>
            . Sobre cookies, consulte la{" "}
            <Link to="/cookies" className="text-brand-primary font-semibold underline underline-offset-2">
              política de cookies
            </Link>
            .
          </p>

          {prefsOpen ? (
            <div className="space-y-3 rounded-xl border border-brand-soft bg-[#fafcfb] p-3">
              {Object.values(COOKIE_CATEGORIES).map((cat) => {
                const checked =
                  cat.id === "essential"
                    ? true
                    : cat.id === "functional"
                      ? draft.functional
                      : cat.id === "analytics"
                        ? draft.analytics
                        : draft.thirdParty;
                return (
                  <label
                    key={cat.id}
                    className="flex items-start gap-3 p-2 rounded-lg hover:bg-white/80"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 accent-brand-primary"
                      checked={checked}
                      disabled={cat.required}
                      onChange={(e) => {
                        if (cat.id === "functional") {
                          setDraft((d) => ({ ...d, functional: e.target.checked }));
                        }
                        if (cat.id === "analytics") {
                          setDraft((d) => ({ ...d, analytics: e.target.checked }));
                        }
                        if (cat.id === "thirdParty") {
                          setDraft((d) => ({ ...d, thirdParty: e.target.checked }));
                        }
                      }}
                    />
                    <span>
                      <span className="block text-sm font-bold text-brand-dark">{cat.label}</span>
                      <span className="block text-xs text-brand-dark/60 leading-relaxed">
                        {cat.description}
                      </span>
                    </span>
                  </label>
                );
              })}
              <p className="text-[10px] text-brand-dark/40 px-2">Versión {COOKIE_POLICY_VERSION}</p>
            </div>
          ) : null}

          <div className="flex flex-col sm:flex-row gap-2">
            {prefsOpen ? (
              <button
                type="button"
                onClick={() => savePreferences(draft)}
                className="flex-1 px-4 py-3 rounded-xl bg-brand-primary text-white text-sm font-bold"
              >
                Guardar preferencias
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="flex-1 px-4 py-3 rounded-xl bg-brand-dark text-white text-sm font-bold"
                >
                  Aceptar todas
                </button>
                <button
                  type="button"
                  onClick={rejectAll}
                  className="flex-1 px-4 py-3 rounded-xl border border-brand-dark text-brand-dark text-sm font-bold"
                >
                  Rechazar todas
                </button>
              </>
            )}
          </div>

          {!prefsOpen ? (
            <button
              type="button"
              onClick={() => setPrefsOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-primary hover:underline"
            >
              <Settings2 size={14} />
              Configurar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setPrefsOpen(false)}
              className="text-xs font-semibold text-brand-dark/55 hover:underline"
            >
              Volver
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
